import { useMemo, useState } from 'react';
import { Link, useParams, useSearchParams } from 'react-router-dom';
import {
  V2Card,
  V2CardHeader,
  V2EmptyState,
  V2FormField,
  V2FormGrid,
  LoadingState,
  StatePanel,
} from '@sakhtar/design-system/react';
import { useApiGet } from '../../lib/useApiGet.js';
import { useFinanceBook } from './useFinanceBook.js';
import { tenantBasePath } from '../../lib/tenant.js';
import { JalaliDateInput } from '../../lib/date/JalaliDateInput.js';
import { displayJalaliDate } from '../../lib/date/displayJalali.js';
import {
  amountColumnHeader,
  formatRialFa,
  journalHref,
  type AmountDisplayUnit,
} from '../../lib/finance-display.js';
import { useAmountDisplay } from '../../lib/tenantSettings.js';
import type {
  AccountListResponse,
  Dimension,
  DimensionValue,
  LedgerResponse,
} from '../../lib/types/finance.js';

type LedgerMode = 'general' | 'subsidiary' | 'detail';

const MODE_TITLE: Record<LedgerMode, string> = {
  general: 'دفتر کل',
  subsidiary: 'دفتر معین',
  detail: 'دفتر تفصیلی',
};

/** Signed running balance spoken with «بد»/«بس», never colour alone. */
export function signedBalanceLabel(digits: string, unit: AmountDisplayUnit): string {
  const value = BigInt(digits);
  if (value > 0n) return `${formatRialFa(digits, unit)} بد`;
  if (value < 0n) return `${formatRialFa((-value).toString(), unit)} بس`;
  return '۰';
}

/**
 * UI_REFERENCE_SOURCE:
 * section: Sakhtar Finance.dc.html §11 REPORTS — ledgers (دفاتر کل/معین/تفصیلی, SOT §12.11)
 * archetype: filtered report table + opening row + totals row
 * status: PARITY — `GET .../finance/reports/ledger`, posted-only.
 *   General/subsidiary ledger = one account and all its descendants
 *   (pick a group/کل account for the general ledger, a postable معین
 *   account for the subsidiary one); detail ledger = every posted line
 *   tagged with one floating detail-level value (cost center, project…),
 *   optionally narrowed to one account. Opening and running balances are
 *   server-computed.
 * state reference: ready (nothing picked) · loading · empty · permission_denied ·
 *   network_error · validation
 */
export function LedgerPage() {
  const book = useFinanceBook();
  const { unit } = useAmountDisplay();
  const { slug } = useParams<{ slug: string }>();
  const [params, setParams] = useSearchParams();
  const base = slug ? tenantBasePath(slug) : '';
  const mode = (['general', 'subsidiary', 'detail'] as const).includes(
    params.get('mode') as LedgerMode,
  )
    ? (params.get('mode') as LedgerMode)
    : 'subsidiary';
  const [accountRef, setAccountRef] = useState('');
  const [dimensionId, setDimensionId] = useState('');
  const [dimensionValueId, setDimensionValueId] = useState('');
  const [fromDate, setFromDate] = useState('');
  const [toDate, setToDate] = useState('');

  const accounts = useApiGet<AccountListResponse>(slug ? `${base}/api/v1/finance/accounts` : null, [
    slug,
  ]);
  const dimensions = useApiGet<{ readonly items: readonly Dimension[] }>(
    slug && mode === 'detail' ? `${base}/api/v1/master-data/dimensions?limit=200` : null,
    [slug, mode],
  );
  const values = useApiGet<{ readonly items: readonly DimensionValue[] }>(
    slug && mode === 'detail' && dimensionId
      ? `${base}/api/v1/master-data/dimension-values?dimensionId=${dimensionId}&limit=200`
      : null,
    [slug, mode, dimensionId],
  );

  const accountOptions = useMemo(() => {
    if (accounts.status !== 'ready') return [];
    const items = [...accounts.data.items].sort((a, b) => a.code.localeCompare(b.code));
    if (mode === 'general') return items.filter((a) => !a.isPostable);
    if (mode === 'subsidiary') return items.filter((a) => a.isPostable);
    return items;
  }, [accounts, mode]);

  const query = new URLSearchParams();
  if (mode === 'detail') {
    if (dimensionValueId) query.set('dimensionValueId', dimensionValueId);
    if (accountRef) query.set('accountRef', accountRef);
  } else if (accountRef) {
    query.set('accountRef', accountRef);
  }
  if (fromDate) query.set('fromDate', fromDate);
  if (toDate) query.set('toDate', toDate);
  if (book.bookId) query.set('bookId', book.bookId);
  const ready = book.ready && (mode === 'detail' ? Boolean(dimensionValueId) : Boolean(accountRef));
  const report = useApiGet<LedgerResponse>(
    slug && ready ? `${base}/api/v1/finance/reports/ledger?${query.toString()}` : null,
    [slug, query.toString()],
  );

  if (!slug) return null;

  function switchMode(next: LedgerMode) {
    setAccountRef('');
    setDimensionId('');
    setDimensionValueId('');
    setParams({ mode: next });
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>
      <V2Card>
        <V2CardHeader
          title={MODE_TITLE[mode]}
          subtitle="فقط اسناد قطعی؛ مانده‌ی ابتدای دوره و مانده‌ی جاری"
          actions={
            ready && report.status === 'ready' ? (
              <Link
                className="sk-btn sk-btn--secondary sk-btn--sm"
                to={`${base}/finance/reports/ledger/print?mode=${mode}&${query.toString()}`}
              >
                چاپ
              </Link>
            ) : null
          }
        />
        <div style={{ padding: '0 20px 18px', display: 'flex', flexDirection: 'column', gap: 14 }}>
          <div role="tablist" style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
            {(Object.keys(MODE_TITLE) as LedgerMode[]).map((m) => (
              <button
                key={m}
                type="button"
                role="tab"
                aria-selected={m === mode}
                className={`sk-btn sk-btn--sm ${m === mode ? 'sk-btn--primary' : 'sk-btn--secondary'}`}
                onClick={() => switchMode(m)}
              >
                {MODE_TITLE[m]}
              </button>
            ))}
          </div>
          <V2FormGrid>
            {mode === 'detail' ? (
              <>
                <V2FormField label="سطح تفصیلی" required width="medium">
                  <select
                    className="sk-select"
                    value={dimensionId}
                    onChange={(e) => {
                      setDimensionId(e.target.value);
                      setDimensionValueId('');
                    }}
                  >
                    <option value="">انتخاب سطح</option>
                    {dimensions.status === 'ready' &&
                      dimensions.data.items.map((d) => (
                        <option key={d.dimensionId} value={d.dimensionId}>
                          {d.name}
                        </option>
                      ))}
                  </select>
                </V2FormField>
                <V2FormField label="تفصیلی" required width="medium">
                  <select
                    className="sk-select"
                    value={dimensionValueId}
                    disabled={!dimensionId}
                    onChange={(e) => setDimensionValueId(e.target.value)}
                  >
                    <option value="">انتخاب</option>
                    {values.status === 'ready' &&
                      values.data.items.map((v) => (
                        <option key={v.dimensionValueId} value={v.dimensionValueId}>
                          {v.code} — {v.name}
                        </option>
                      ))}
                  </select>
                </V2FormField>
              </>
            ) : null}
            <V2FormField
              label={
                mode === 'general'
                  ? 'حساب کل'
                  : mode === 'subsidiary'
                    ? 'حساب معین'
                    : 'حساب (اختیاری)'
              }
              required={mode !== 'detail'}
              width="wide"
            >
              <select
                className="sk-select"
                value={accountRef}
                onChange={(e) => setAccountRef(e.target.value)}
              >
                <option value="">{mode === 'detail' ? 'همهٔ حساب‌ها' : 'انتخاب حساب'}</option>
                {accountOptions.map((a) => (
                  <option key={a.accountId} value={a.code}>
                    {a.code} — {a.name}
                  </option>
                ))}
              </select>
            </V2FormField>
            <V2FormField label="از تاریخ" width="medium">
              <JalaliDateInput value={fromDate} onChange={setFromDate} />
            </V2FormField>
            <V2FormField label="تا تاریخ" width="medium">
              <JalaliDateInput value={toDate} onChange={setToDate} />
            </V2FormField>
          </V2FormGrid>
        </div>
      </V2Card>

      {!ready ? (
        <V2EmptyState
          icon="finance"
          title={mode === 'detail' ? 'تفصیلی را انتخاب کنید' : 'حساب را انتخاب کنید'}
        />
      ) : null}
      {ready && report.status === 'loading' ? <LoadingState /> : null}
      {ready && report.status === 'error' ? (
        <StatePanel
          variant={report.error.status === 403 ? 'permission_denied' : 'network_error'}
          title={
            report.error.status === 403
              ? 'دسترسی ندارید'
              : report.error.status === 422
                ? 'بازهٔ تاریخ نامعتبر است یا گزارش بیش از حد بزرگ است'
                : 'گزارش قابل نمایش نیست'
          }
        />
      ) : null}
      {ready && report.status === 'ready' ? (
        <V2Card>
          <V2CardHeader
            title={
              report.data.dimensionValue
                ? `${report.data.dimensionValue.dimensionName}: ${report.data.dimensionValue.name}`
                : report.data.account
                  ? `${report.data.account.code} — ${report.data.account.name}`
                  : MODE_TITLE[mode]
            }
            subtitle={`${report.data.rows.length.toLocaleString('fa-IR')} ردیف`}
          />
          <div className="sk-table-scroll">
            <table className="sk-table sk-table--compact">
              <thead>
                <tr>
                  <th style={{ width: 100 }}>تاریخ</th>
                  <th style={{ width: 110 }}>شمارهٔ سند</th>
                  {mode !== 'subsidiary' ? <th>حساب</th> : null}
                  <th>شرح</th>
                  <th style={{ width: 130 }}>{amountColumnHeader('بدهکار', unit)}</th>
                  <th style={{ width: 130 }}>{amountColumnHeader('بستانکار', unit)}</th>
                  <th style={{ width: 150 }}>{amountColumnHeader('مانده', unit)}</th>
                </tr>
              </thead>
              <tbody>
                <tr style={{ background: 'var(--sk-bg-canvas)' }}>
                  <td colSpan={mode !== 'subsidiary' ? 6 : 5} style={{ fontWeight: 600 }}>
                    مانده از قبل
                  </td>
                  <td className="sk-num" style={{ fontWeight: 600 }}>
                    {signedBalanceLabel(report.data.openingBalanceRial, unit)}
                  </td>
                </tr>
                {report.data.rows.map((row) => (
                  <tr key={`${row.journalId}-${row.rowNumber}`}>
                    <td className="sk-num">{displayJalaliDate(row.businessDate)}</td>
                    <td className="ltr sk-num">
                      <Link to={journalHref(base, row.journalId)}>{row.documentNumber}</Link>
                    </td>
                    {mode !== 'subsidiary' ? (
                      <td>
                        <span className="ltr sk-num" style={{ color: 'var(--sk-fg-muted)' }}>
                          {row.accountRef}
                        </span>{' '}
                        {row.accountName}
                      </td>
                    ) : null}
                    <td>{row.description ?? '—'}</td>
                    <td className="sk-num">
                      {BigInt(row.debitRial) > 0n ? formatRialFa(row.debitRial, unit) : '—'}
                    </td>
                    <td className="sk-num">
                      {BigInt(row.creditRial) > 0n ? formatRialFa(row.creditRial, unit) : '—'}
                    </td>
                    <td className="sk-num">{signedBalanceLabel(row.balanceRial, unit)}</td>
                  </tr>
                ))}
              </tbody>
              <tfoot>
                <tr>
                  <td colSpan={mode !== 'subsidiary' ? 4 : 3} style={{ fontWeight: 700 }}>
                    جمع گردش و مانده‌ی پایان
                  </td>
                  <td className="sk-num" style={{ fontWeight: 700 }}>
                    {formatRialFa(report.data.totals.debitRial, unit)}
                  </td>
                  <td className="sk-num" style={{ fontWeight: 700 }}>
                    {formatRialFa(report.data.totals.creditRial, unit)}
                  </td>
                  <td className="sk-num" style={{ fontWeight: 700 }}>
                    {signedBalanceLabel(report.data.totals.closingBalanceRial, unit)}
                  </td>
                </tr>
              </tfoot>
            </table>
          </div>
        </V2Card>
      ) : null}
    </div>
  );
}
